import { useCallback, useEffect, useRef, useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, InputLabel, MenuItem, Select, TextField, Tooltip,
} from "@mui/material";
import FileUploadIcon       from "@mui/icons-material/FileUpload";
import AccountBalanceIcon   from "@mui/icons-material/AccountBalance";
import PeopleAltIcon        from "@mui/icons-material/PeopleAlt";
import ReceiptLongIcon      from "@mui/icons-material/ReceiptLong";
import CheckCircleIcon      from "@mui/icons-material/CheckCircle";
import { ZOIS_BO_KEY } from "components/Settings/DictionariesTab/WpkGminyTab";
import { useOpeningBalance, useSaveOpeningBalance, useSaveJournal, useJournal, useSaveBalanceSheets } from "api/accountingService";
import postingSchemeService from "api/postingSchemeService";
import { ENotificationType, notification } from "utils/notification";
import { deriveBalanceFromAccounts } from "utils/deriveBalance";
import contractorsService, { IContractorImportItem } from "api/contractorsService";
import {
  MIGRATION_SYSTEM_KEY,
  GENERIC_JOURNAL_HINTS,
  IFkJournalHints,
  getParser,
  applyCodeNorm,
  isValidCode,
  detectCol,
  detectEncoding,
  detectCsvSep,
} from "config/fkParsers";
import { resilientLocalStorage } from "utils/resilientLocalStorage";

// ── Wykrywanie właściwego wiersza nagłówkowego ────────────────────────────────
// Fakir (i inne systemy) mogą eksportować CSV z wierszem tytułowym PRZED nagłówkami
// (np. "Wydruk z dziennika głównego za rok 2024"). Skanujemy pierwsze 15 wierszy
// i wybieramy ten, który najlepiej pasuje do oczekiwanych nagłówków kolumn.

function findHeaderRowIdx(raw: CellValue[][], hints: IFkJournalHints): number {
  const keywords = [
    ...hints.nrDokumentu,
    ...hints.dataKsiegowania,
    ...hints.opis,
    ...hints.kontoWn,
    ...hints.kontoMa,
    ...hints.kwota,
  ].map((p) => p.toLowerCase().trim()).filter((p) => p.length >= 3);

  let bestRow = 0;
  let bestScore = -1;

  for (let i = 0; i < Math.min(raw.length, 15); i++) {
    const cells = (raw[i] as CellValue[])
      .map((c) => String(c ?? "").toLowerCase().trim())
      .filter((c) => c.length >= 2);

    if (cells.length < 3) continue;

    let score = 0;
    for (const cell of cells) {
      for (const kw of keywords) {
        if (cell === kw || cell.includes(kw) || (cell.length >= 3 && kw.includes(cell))) {
          score++;
          break;
        }
      }
    }
    if (score > bestScore) { bestScore = score; bestRow = i; }
  }

  return bestRow;
}

/**
 * Parser strukturalnego dziennika Fakir (format XLS/XLSX).
 *
 * Fakir eksportuje dziennik jako STRUKTURALNY dokument, NIE standardowy CSV:
 * - Wiersz z " - " w kolumnie A i "Numer" w kolumnie B = identyfikator subksięgi
 * - Wiersz z "XXXXX/TYP/NN" w kolumnie B i "Opis:" w kolumnie G = nagłówek dokumentu
 * - Wiersz z małą liczbą w A i kwotą w B = wiersz dekretu (Konto Wn=C, Konto Ma=E)
 * - Wiersz "Razem w okresie" = podsumowanie subksięgi (pomijany)
 *
 * Zwraca pre-znormalizowane rows z nagłówkami pasującymi do hints Fakir
 * ("Nr dokumentu", "Konto Wn", "Konto Ma"…) — detectMapping znajdzie je bez problemu.
 * Zwraca null jeśli plik nie jest w tym formacie.
 */
function parseFakirJournal(
  raw: CellValue[][],
): { headers: string[]; rows: Record<string, unknown>[] } | null {
  // Sygnatura formatu: wiersz 0, kolumna A zawiera " - ", kolumna B = "Numer"
  const a0 = String(raw[0]?.[0] ?? "").trim();
  const b0 = String(raw[0]?.[1] ?? "").trim();
  if (!a0.includes(" - ") || b0 !== "Numer") return null;

  // Wzorzec numeru dziennikowego: "XXXXX/TYP/NN" (np. "00014/WB/26", "00050/FZK/26")
  const docHdrRe = /^\d+\/[A-Z]+\/\d+$/;

  const _date = (v: string): string => {
    const m = v.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    return m ? `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}` : "";
  };

  const _isNonZero = (s: string): boolean => {
    if (!s) return false;
    const n = parseFloat(s.replace(/,/g, "").replace(/\s/g, ""));
    return !isNaN(n) && n !== 0;
  };

  const rows: Record<string, unknown>[] = [];
  let currentDoc: { nr: string; date: string; typ: string; opis: string } | null = null;

  for (const rawRow of raw) {
    const r = rawRow as CellValue[];
    const a = String(r[0] ?? "").trim();
    const b = String(r[1] ?? "").trim();
    const c = String(r[2] ?? "").trim();
    const d = String(r[3] ?? "").trim();
    const e = String(r[4] ?? "").trim();
    const f = String(r[5] ?? "").trim();
    const g = String(r[6] ?? "").trim();

    if (!a && !b && !c && !d && !e && !f && !g) continue; // pusty wiersz

    // Identyfikator subksięgi — ignoruj, ustaw bieżący typ sekcji
    if (a.includes(" - ") && b === "Numer") { currentDoc = null; continue; }

    // Wiersz podsumowania ("Razem w okresie") lub objaśnień końcowych
    if (a.toLowerCase().startsWith("razem") || a.toLowerCase().startsWith("objaśn")) {
      currentDoc = null; continue;
    }

    // Nagłówek dokumentu: B = numer dziennikowy, G zaczyna się od "Opis:"
    if (docHdrRe.test(b) && g.toLowerCase().startsWith("opis:")) {
      const typ = (b.match(/\/([A-Z]+)\//) ?? [])[1] ?? "";
      currentDoc = {
        nr:   b,
        date: _date(d),
        typ,
        opis: g.slice(g.indexOf(":") + 1).trim(),
      };
      continue;
    }

    // Wiersz dekretu: A = liczba porządkowa, B = kwota DT, C = Konto Wn, D = kwota CT, E = Konto Ma
    if (currentDoc && /^\d+$/.test(a) && b !== "Numer") {
      // Kwota: użyj strony DT jeśli niezerowa, inaczej CT
      const kwota = _isNonZero(b) ? b : d;
      rows.push({
        "Nr dokumentu":     currentDoc.nr,
        "Data":             currentDoc.date,
        "Symbol dziennika": currentDoc.typ,
        "Opis":             f || currentDoc.opis,
        "Konto Wn":         c,
        "Konto Ma":         e,
        "Kwota":            kwota,
      });
    }
  }

  if (rows.length === 0) return null;

  return {
    headers: ["Nr dokumentu", "Data", "Symbol dziennika", "Opis", "Konto Wn", "Konto Ma", "Kwota"],
    rows,
  };
}

/**
 * Zwraca scalone nagłówki i indeks pierwszego wiersza danych.
 *
 * Fakir (i inne systemy) eksportuje CSV z dwupoziomowym nagłówkiem:
 *   Wiersz A: "Nr"      | ""     | ""     | "Konto" | "Konto" | "Kwota" | "Kwota"
 *   Wiersz B: "dokum."  | "Data" | "Opis" | "Wn"    | "Ma"    | "DT"    | "CT"
 * Scalony:    "Nr dokum."| "Data" | "Opis" | "Konto Wn"|"Konto Ma"|"Kwota DT"|"Kwota CT"
 *
 * Scalanie zachodzi gdy wiersz PRZED najlepiej dopasowanym ma co najmniej 2 niepuste
 * komórki i w co najmniej jednej kolumnie uzupełnia krótki (≤ 3 znaki) fragment.
 */
function readJournalHeaders(
  raw: CellValue[][],
  hints: IFkJournalHints,
): { headers: string[]; dataStartIdx: number } {
  const baseIdx = findHeaderRowIdx(raw, hints);
  const baseRow = (raw[baseIdx] as CellValue[]).map((c) => String(c ?? "").trim());

  if (baseIdx > 0) {
    const prevRow = (raw[baseIdx - 1] as CellValue[]).map((c) => String(c ?? "").trim());
    const prevNonEmpty = prevRow.filter((c) => c.length > 0).length;

    // Scalaj jeśli górny wiersz ma treść i uzupełnia krótkie (≤ 3 znaki) fragmenty dolnego
    const hasComplement =
      prevNonEmpty >= 2 &&
      prevRow.some((top, i) => top.length > 0 && (baseRow[i] ?? "").length <= 3);

    if (hasComplement) {
      const maxLen = Math.max(prevRow.length, baseRow.length);
      const merged: string[] = [];
      for (let i = 0; i < maxLen; i++) {
        const top = prevRow[i] ?? "";
        const bot = baseRow[i] ?? "";
        merged.push(top && bot ? `${top} ${bot}` : top || bot);
      }
      return { headers: merged, dataStartIdx: baseIdx + 1 };
    }
  }

  return { headers: baseRow, dataStartIdx: baseIdx + 1 };
}

// ── Systemy FK ────────────────────────────────────────────────────────────────

interface IFinSystem { id: string; name: string; vendor: string; }

const FIN_SYSTEMS: IFinSystem[] = [
  { id: "wapro_fakir",    name: "Fakir",                 vendor: "WAPRO" },
  { id: "comarch_optima", name: "ERP Optima",            vendor: "Comarch" },
  { id: "comarch_xl",     name: "CDN XL",                vendor: "Comarch" },
  { id: "symfonia_fk",    name: "Finanse i Księgowość",  vendor: "Symfonia" },
  { id: "enova365",       name: "enova365",              vendor: "Soneta" },
  { id: "sap",            name: "SAP",                   vendor: "SAP" },
  { id: "ms_dynamics",    name: "Dynamics 365",          vendor: "Microsoft" },
  { id: "rewizor",        name: "Rewizor GT",            vendor: "Insert" },
  { id: "rachmistrz",     name: "Rachmistrz GT",         vendor: "Insert" },
  { id: "streamsoft",     name: "Prestiż",               vendor: "Streamsoft" },
  { id: "raks",           name: "RAKS SQL",              vendor: "RAKS" },
  { id: "other",          name: "Inny system",           vendor: "" },
];

// ── Klucze localStorage ───────────────────────────────────────────────────────

// MIGRATION_SYSTEM_KEY pochodzi z config/fkParsers — wspólna stała dla całej aplikacji
const CONTRACTORS_FILE_KEY  = "zois_contractors_file";
const CONTRACTORS_ROWS_KEY  = "zois_contractors_rows";  // { entries: [{code,name}] }
const JOURNALS_FILE_KEY     = "zois_journals_file";
const JOURNALS_ROWS_KEY     = "zois_journals_rows";

// ── Parsery CSV/XLS ───────────────────────────────────────────────────────────

type CellValue = string | number | boolean | null | undefined;
const _cs  = (v: CellValue): string => (v == null ? "" : String(v).trim());
const _num = (v: CellValue): number => {
  if (typeof v === "number") return v;
  const s = _cs(v).replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  return parseFloat(s) || 0;
};

// ── Typy ─────────────────────────────────────────────────────────────────────

interface IBoEntry        { code: string; name: string; boWn: number; boMa: number; saldoWn: number; saldoMa: number; }
interface IBoImportPreview { fileName: string; entries: IBoEntry[]; }
interface IBoFileInfo     { fileName: string; boDate: string; importedAt: string; }
interface IGenericFileInfo { fileName: string; importedAt: string; count: number; }

// ── Dane z localStorage ───────────────────────────────────────────────────────

const loadBoFileInfo = (): IBoFileInfo | null => {
  try {
    const raw = localStorage.getItem(ZOIS_BO_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<IBoFileInfo & { entries: unknown[] }>;
    const hasEntries = Array.isArray(parsed.entries) && parsed.entries.length > 0;
    if (!parsed.fileName && !hasEntries && !parsed.importedAt) return null;
    return { fileName: parsed.fileName ?? "", boDate: parsed.boDate ?? "", importedAt: parsed.importedAt ?? "" };
  } catch { return null; }
};

const loadGenericFileInfo = (key: string): IGenericFileInfo | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const p = JSON.parse(raw) as Partial<IGenericFileInfo>;
    if (!p.fileName) return null;
    return { fileName: p.fileName, importedAt: p.importedAt ?? "", count: p.count ?? 0 };
  } catch { return null; }
};

const loadSelectedSystem = (): string | null => {
  try {
    const raw = localStorage.getItem(MIGRATION_SYSTEM_KEY);
    return raw ? (JSON.parse(raw) as { id: string }).id : null;
  } catch {
    return null;
  }
};

const plDt = (iso: string) => iso ? new Date(iso).toLocaleString("pl-PL") : "";

// ── Subkomponenty ─────────────────────────────────────────────────────────────

const SectionLabel = ({ step, children }: { step: number; children: React.ReactNode }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
    <div style={{
      width: 22, height: 22, borderRadius: "50%", background: "#5d49c7",
      color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0,
    }}>
      {step}
    </div>
    <span style={{ fontSize: 12, fontWeight: 700, color: "#3d3a4a", textTransform: "uppercase", letterSpacing: "0.06em" }}>
      {children}
    </span>
  </div>
);

interface ImportRowProps {
  icon: React.ReactNode;
  title: string;
  hasData: boolean;
  metaLines: string[];
  loading: boolean;
  onImport: () => void;
}

const ImportRow = ({ icon, title, hasData, metaLines, loading, onImport }: ImportRowProps) => (
  <div style={{
    display: "flex",
    alignItems: "center",
    gap: 16,
    padding: "12px 16px",
    border: `1px solid ${hasData ? "rgba(93,73,199,0.28)" : "rgba(0,0,0,0.1)"}`,
    borderRadius: 8,
    background: hasData ? "rgba(93,73,199,0.03)" : "#fafafa",
  }}>
    {/* Ikona + tytuł */}
    <div style={{ display: "flex", alignItems: "center", gap: 8, width: 220, flexShrink: 0 }}>
      {icon}
      <span style={{ fontWeight: 600, fontSize: 13, color: "#3d3a4a" }}>{title}</span>
      {hasData && <CheckCircleIcon sx={{ fontSize: 15, color: "#5d49c7" }} />}
    </div>

    {/* Informacje */}
    <div style={{ flex: 1, minWidth: 0 }}>
      {hasData ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {metaLines.map((line, i) => (
            <div key={i} style={{ fontSize: i === 0 ? 12 : 11, color: i === 0 ? "#5f5c71" : "#9a91aa" }}>
              {line}
            </div>
          ))}
        </div>
      ) : (
        <span style={{ fontSize: 12, color: "#bbb5c9", fontStyle: "italic" }}>
          Brak wczytanego pliku
        </span>
      )}
    </div>

    {/* Przycisk */}
    <Tooltip title={loading ? "Wczytywanie…" : hasData ? "Zastąp plik nowym" : `Wczytaj plik ${title}`}>
      <span style={{ flexShrink: 0 }}>
        <Button
          variant={hasData ? "outlined" : "contained"}
          size="small"
          startIcon={<FileUploadIcon />}
          onClick={onImport}
          disabled={loading}
          sx={{
            fontSize: 12, whiteSpace: "nowrap",
            ...(hasData ? {} : { background: "#5d49c7", "&:hover": { background: "#4a38a8" } }),
          }}
        >
          {loading ? "Wczytywanie…" : hasData ? "Wymień plik" : "Wczytaj plik"}
        </Button>
      </span>
    </Tooltip>
  </div>
);

// ── Główny komponent ──────────────────────────────────────────────────────────

const MigrationTab = () => {
  // System źródłowy
  const [selectedSystemId, setSelectedSystemId] = useState<string | null>(loadSelectedSystem);

  // Bilans otwarcia
  const importBoRef                         = useRef<HTMLInputElement>(null);
  const [boImport, setBoImport]             = useState<IBoImportPreview | null>(null);
  const [boImporting, setBoImporting]       = useState(false);
  const { mutateAsync: saveBalanceSheets }  = useSaveBalanceSheets();
  const [boDate, setBoDate]                 = useState("");
  const [boFileInfo, setBoFileInfo]         = useState<IBoFileInfo | null>(loadBoFileInfo);
  const { mutate: saveBoMutation }          = useSaveOpeningBalance();
  const { mutateAsync: importContractors }  = contractorsService.useImport();
  const { mutateAsync: saveJournal }        = useSaveJournal();
  const { mutateAsync: backfillSchemes, isLoading: backfillLoading } = postingSchemeService.useBackfill();

  // Wywiedź i zapisz schematy księgowania dla wszystkich kontrahentów, którzy
  // mają wyprowadzalny układ dekretu w dzienniku (pomija już zapisane).
  const handleBackfillSchemes = async () => {
    try {
      const r = await backfillSchemes();
      notification({
        title: "Schematy księgowania",
        text: `Zapisano ${r.created} nowych schematów (pominięto istniejące: ${r.skipped}; kontrahentów z historią: ${r.contractors}).`,
        type: ENotificationType.INFO,
      });
    } catch {
      notification({
        title: "Schematy księgowania",
        text: "Nie udało się wywieść schematów z dziennika.",
        type: ENotificationType.ERROR,
      });
    }
  };
  const boYear = boFileInfo?.boDate ? new Date(boFileInfo.boDate).getFullYear() : new Date().getFullYear();
  const { data: boApiData }                 = useOpeningBalance(boYear);
  const hasBeData                           = (boApiData?.entries?.length ?? 0) > 0;
  const hasBoData                           = !!boFileInfo || hasBeData;
  const boImportedAt                        = boFileInfo?.importedAt || boApiData?.importedAt || "";

  // Kartoteka kontrahentów
  const contractorsRef                      = useRef<HTMLInputElement>(null);
  const [contractorsLoading, setContractorsLoading] = useState(false);
  const [contractorsInfo, setContractorsInfo] = useState<IGenericFileInfo | null>(() => loadGenericFileInfo(CONTRACTORS_FILE_KEY));

  // Dzienniki księgowań
  const journalsRef                         = useRef<HTMLInputElement>(null);
  const [journalsLoading, setJournalsLoading] = useState(false);
  const [journalsInfo, setJournalsInfo]     = useState<IGenericFileInfo | null>(() => loadGenericFileInfo(JOURNALS_FILE_KEY));

  // ── Rehydratacja kafelków z BE (źródło prawdy = DB) ────────────────────────
  // Lustro w localStorage znika po twardym resecie/„Clear site data", ale dane
  // SĄ w bazie. Jak Bilans otwarcia (useOpeningBalance), odtwarzamy stan kafelków
  // Kontrahenci/Dzienniki z BE, gdy brak lustra — inaczej wyglądałyby na puste
  // mimo danych w DB.
  const { data: journalApi }                = useJournal();
  const { data: contractorsList }           = contractorsService.useList();

  useEffect(() => {
    if (journalsInfo) return;
    if (journalApi && journalApi.rowCount > 0) {
      setJournalsInfo({
        fileName: journalApi.fileName ?? "",
        importedAt: journalApi.importedAt ?? "",
        count: journalApi.rowCount,
      });
    }
  }, [journalApi, journalsInfo]);

  useEffect(() => {
    if (contractorsInfo) return;
    if (contractorsList && contractorsList.length > 0) {
      // BE nie przechowuje metadanych importu (nazwa pliku/data) — pokazujemy
      // przynajmniej liczność z bazy, co potwierdza, że kartoteka jest wczytana.
      setContractorsInfo({ fileName: "", importedAt: "", count: contractorsList.length });
    }
  }, [contractorsList, contractorsInfo]);

  // ── Handlery ─────────────────────────────────────────────────────────────

  const handleSystemSelect = (id: string) => {
    const next = selectedSystemId === id ? null : id;
    setSelectedSystemId(next);
    try {
      if (next) localStorage.setItem(MIGRATION_SYSTEM_KEY, JSON.stringify({ id: next }));
      else localStorage.removeItem(MIGRATION_SYSTEM_KEY);
    } catch { /* ignore */ }
  };

  const handleBoFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setBoImporting(true);
    try {
      const parser = getParser(selectedSystemId);
      const XLSX = await import("xlsx");
      const isCsv = /\.(csv|txt)$/i.test(file.name);
      const buffer = await file.arrayBuffer();
      let raw: CellValue[][];
      if (isCsv) {
        const bytes = new Uint8Array(buffer);
        const encoding = detectEncoding(bytes, parser);
        const text = new TextDecoder(encoding).decode(buffer);
        const firstLine = text.split(/\r?\n/)[0] ?? "";
        const sep = detectCsvSep(firstLine, parser);
        const wb = XLSX.read(text, { type: "string", FS: sep });
        raw = XLSX.utils.sheet_to_json<CellValue[]>(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: "" });
      } else {
        const wb = XLSX.read(buffer, { type: "array" });
        raw = XLSX.utils.sheet_to_json<CellValue[]>(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: "" });
      }
      if (!raw || raw.length < 2) { setBoImporting(false); return; }
      const headers = (raw[0] as CellValue[]).map((h) => _cs(h));
      const dataRows = raw.slice(1) as CellValue[][];

      // Wykrycie kolumn wg zasad wybranego systemu FK
      const hints = parser.columnHints;
      let codeCol = detectCol(headers, hints.code);
      let nameCol = detectCol(headers, hints.name);
      if (codeCol === -1) codeCol = 0;
      if (nameCol === -1 || nameCol === codeCol) nameCol = codeCol === 0 ? 1 : 0;

      // Plik ZOiS jest WIELOSEKCYJNY (BO Wn/Ma, Obroty Wn/Ma, Salda Wn/Ma). Bierzemy
      // parę kolumn BILANSU OTWARCIA: preferujemy parę oznaczoną „BO/otwarcie",
      // a spośród reszty tę, która się bilansuje (ΣWn≈ΣMa). Chroni przed pobraniem
      // niewłaściwej (rozjechanej) pary kolumn.
      const lower = headers.map((h) => h.toLowerCase());
      const isBoLabel = (h: string) =>
        /(^|[^a-z])(bo|b\.o\.)([^a-z]|$)|otwarc|otwar\.|pocz(a|ą)tk|bilans otw|saldo pocz|saldo otw/.test(h);
      const idxs = headers.map((_, i) => i);
      const wnCols = idxs.filter((i) => i !== codeCol && i !== nameCol && /(^|[^a-z])wn([^a-z]|$)|winien|debet|(^|[^a-z])dt([^a-z]|$)/.test(lower[i]));
      const maCols = idxs.filter((i) => i !== codeCol && i !== nameCol && /(^|[^a-z])ma([^a-z]|$)|kredyt|(^|[^a-z])ct([^a-z]|$)/.test(lower[i]));
      const colSum = (idx: number) => dataRows.reduce((sum, r) => sum + Math.abs(_num(r[idx])), 0);

      let boWnCol = -1;
      let boMaCol = -1;
      if (wnCols.length && maCols.length) {
        const n = Math.min(wnCols.length, maCols.length);
        const pairs: { wn: number; ma: number; bo: boolean; diff: number }[] = [];
        for (let k = 0; k < n; k++) {
          const wn = wnCols[k];
          const ma = maCols[k];
          pairs.push({ wn, ma, bo: isBoLabel(lower[wn]) || isBoLabel(lower[ma]), diff: Math.abs(colSum(wn) - colSum(ma)) });
        }
        pairs.sort((a, b) => (a.bo === b.bo ? a.diff - b.diff : a.bo ? -1 : 1));
        boWnCol = pairs[0].wn;
        boMaCol = pairs[0].ma;
      }
      if (boWnCol === -1) { boWnCol = detectCol(headers, hints.boWn); if (boWnCol === -1) boWnCol = 2; }
      if (boMaCol === -1) { boMaCol = detectCol(headers, hints.boMa); if (boMaCol === -1) boMaCol = 3; }

      // Kolumny SALDA (Saldo Dt/Ct) — salda bieżące do rejestru „Bilanse" (nie BO).
      const notBo = (i: number) => !/(pocz|otw|\bbo\b|b\.o\.|otwarc)/.test(lower[i]);
      const saldoWnCol = idxs.find((i) => i !== codeCol && i !== nameCol && /saldo/.test(lower[i]) && notBo(i)
        && /(^|[^a-z])wn([^a-z]|$)|winien|debet|(^|[^a-z])dt([^a-z]|$)/.test(lower[i])) ?? -1;
      const saldoMaCol = idxs.find((i) => i !== codeCol && i !== nameCol && /saldo/.test(lower[i]) && notBo(i)
        && /(^|[^a-z])ma([^a-z]|$)|kredyt|(^|[^a-z])ct([^a-z]|$)/.test(lower[i])) ?? -1;

      const rawEntries: IBoEntry[] = dataRows
        .map((row) => ({
          code: applyCodeNorm(_cs(row[codeCol]), parser),
          name: _cs(row[nameCol]),
          boWn: _num(row[boWnCol]),
          boMa: _num(row[boMaCol]),
          saldoWn: saldoWnCol >= 0 ? _num(row[saldoWnCol]) : _num(row[boWnCol]),
          saldoMa: saldoMaCol >= 0 ? _num(row[saldoMaCol]) : _num(row[boMaCol]),
        }))
        .filter((a) => isValidCode(a.code, parser));

      // Twarda deduplikacja po kodzie (plik ZOiS bywa zdublowany / z sekcjami).
      const seen = new Map<string, IBoEntry>();
      for (const e of rawEntries) { if (!seen.has(e.code)) seen.set(e.code, e); }
      const entries = [...seen.values()];

      // Walidacja: bilans OTWARCIA (z kolumn BO) powinien się zgadzać (aktywa = pasywa).
      const chk = deriveBalanceFromAccounts(
        entries.map((e) => ({ code: e.code, name: e.name, boWn: e.boWn, boMa: e.boMa })),
        new Date().getFullYear(),
      );
      const ctrl = chk.length ? chk[0].totalAssets - chk[0].totalLiabilities : 0;
      if (entries.length && Math.abs(ctrl) > 1) {
        notification({
          title: "Bilans",
          text: `Uwaga: bilans otwarcia (z BO) nie schodzi do zera — kontrola Aktywa − Pasywa = ${ctrl.toLocaleString("pl-PL")}. Sprawdź plik/kolumny.`,
          type: ENotificationType.ERROR,
        });
      }
      setBoImport({ fileName: file.name, entries });
    } catch (err) {
      console.error("[ZOiS] błąd importu:", err);
    } finally {
      setBoImporting(false);
    }
  };

  const handleBoConfirm = useCallback(() => {
    if (!boImport || !boDate) return;
    const year = new Date(boDate).getFullYear();
    const { entries, fileName } = boImport;
    saveBoMutation({ year, entries: entries.map((e) => ({ code: e.code, name: e.name ?? "", boWn: e.boWn, boMa: e.boMa })) });
    // Bilans otwarcia (aktywa/pasywa) z kolumn BO → rejestr „Bilanse".
    // BO jest stabilne i zbilansowane (bliźniacze syntetyki mają identyczne kwoty),
    // dlatego dedup po nazwie daje kontrolę A−P = 0. Salda bieżące bywają rozjechane
    // o grosze (persaldo) i pozostawiały drobną nierównowagę.
    const balances = deriveBalanceFromAccounts(
      entries.map((e) => ({ code: e.code, name: e.name, boWn: e.boWn, boMa: e.boMa })),
      year,
    );
    // Etykieta okresu wg wybranej daty BO, np. „Bilans otwarcia na 1 lipca 2026".
    const boDateLabel = new Date(boDate).toLocaleDateString("pl-PL", { day: "numeric", month: "long", year: "numeric" });
    if (balances.length) {
      balances[0].label = `Bilans otwarcia na ${boDateLabel}`;
      saveBalanceSheets(balances).catch(() => {});
    }
    const importedAt = new Date().toISOString();
    try { localStorage.setItem(ZOIS_BO_KEY, JSON.stringify({ importedAt, fileName, boDate, entries })); } catch { /* ignore */ }
    setBoFileInfo({ fileName, boDate, importedAt });
    window.dispatchEvent(new Event("zois_bo_updated"));
    setBoImport(null);
  }, [boImport, boDate, saveBoMutation, saveBalanceSheets]);

  const handleContractorsChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setContractorsLoading(true);
    try {
      const parser = getParser(selectedSystemId);
      const XLSX = await import("xlsx");
      const isCsv = /\.(csv|txt)$/i.test(file.name);
      const buffer = await file.arrayBuffer();
      let raw: CellValue[][];
      if (isCsv) {
        const bytes = new Uint8Array(buffer);
        const encoding = detectEncoding(bytes, parser);
        const text = new TextDecoder(encoding).decode(buffer);
        const firstLine = text.split(/\r?\n/)[0] ?? "";
        const sep = detectCsvSep(firstLine, parser);
        const wb = XLSX.read(text, { type: "string", FS: sep });
        raw = XLSX.utils.sheet_to_json<CellValue[]>(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: "" });
      } else {
        const wb = XLSX.read(buffer, { type: "array" });
        raw = XLSX.utils.sheet_to_json<CellValue[]>(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: "" });
      }
      if (!raw || raw.length < 2) return;
      const headers = (raw[0] as CellValue[]).map((h) => _cs(h));
      const dataRows = raw.slice(1) as CellValue[][];
      const hints = parser.columnHints;
      // Kolumny wykrywamy po nagłówkach. Konto FK (analityka / „Numer analityki")
      // bywa puste — NIE wymuszamy wtedy kolumny 0 (inaczej braliśmy Id/kod pocztowy
      // jako konto). NIP wykrywamy osobno, bo służy do deduplikacji na liście.
      const codeCol = detectCol(headers, hints.code);
      let nameCol = detectCol(headers, hints.name);
      const nipCol     = detectCol(headers, ["nip"]);
      const fullNameCol = detectCol(headers, ["nazwa pe", "pełna nazwa", "nazwa pelna"]);
      const regonCol   = detectCol(headers, ["regon"]);
      const peselCol   = detectCol(headers, ["pesel"]);
      const postalCol  = detectCol(headers, ["kod pocz", "kod-pocz"]);
      const cityCol    = detectCol(headers, ["miejscowo", "miasto"]);
      const streetCol  = detectCol(headers, ["ulica", "adres"]);
      if (nameCol === -1) nameCol = nipCol === 0 ? 1 : 0;
      const cleanNip = (s: string) => s.replace(/[\s.-]/g, "").toUpperCase();
      const composeAddress = (row: CellValue[]) => {
        const street = streetCol >= 0 ? _cs(row[streetCol]) : "";
        const postal = postalCol >= 0 ? _cs(row[postalCol]) : "";
        const city   = cityCol   >= 0 ? _cs(row[cityCol])   : "";
        return [street, [postal, city].filter(Boolean).join(" ")].filter(Boolean).join(", ");
      };

      // Pełny rekord (do zapisu w DB) + wąski wpis {code,name,nip} (lustro localStorage).
      const records = dataRows
        .map((row) => ({
          name:     _cs(row[nameCol]),
          fullName: fullNameCol >= 0 ? _cs(row[fullNameCol]) : "",
          nip:      nipCol   >= 0 ? cleanNip(_cs(row[nipCol]))   : "",
          regon:    regonCol >= 0 ? _cs(row[regonCol]).replace(/\D/g, "") : "",
          pesel:    peselCol >= 0 ? _cs(row[peselCol]).replace(/\D/g, "") : "",
          address:  composeAddress(row),
          code:     codeCol  >= 0 ? applyCodeNorm(_cs(row[codeCol]), parser) : "",
        }))
        .filter((r) => r.name);

      if (records.length === 0) {
        console.warn(
          `[Migracja] Plik „${file.name}": nie znaleziono żadnych wierszy kontrahentów ` +
          `(kolumna nazwa=#${nameCol}, nip=#${nipCol}, konto=#${codeCol}). Sprawdź nagłówki / wybrany system FK.`,
        );
        alert("Nie udało się odczytać żadnych kontrahentów z pliku. Sprawdź format pliku i wybrany system FK.");
        return;
      }

      // Zapis do DB (trwała kartoteka) — upsert po NIP/PESEL/REGON/IBAN.
      const importItems: IContractorImportItem[] = records.map((r) => ({
        Name: r.name,
        FullName: r.fullName || undefined,
        NIP: r.nip || undefined,
        REGON: r.regon || undefined,
        PESEL: r.pesel || undefined,
        Address: r.address || undefined,
        AccountingCode: r.code || undefined,
      }));
      try {
        const res = await importContractors(importItems);
        console.info(`[Migracja] kontrahenci zapisani w DB: utworzono ${res.Created}, uzupełniono ${res.Updated}, pominięto ${res.Skipped}.`);
      } catch (err) {
        console.error("[Migracja] nie udało się zapisać kontrahentów w DB:", err);
        alert("Nie udało się zapisać kontrahentów w bazie. Sprawdź połączenie i spróbuj ponownie.");
        return;
      }

      const importedAt = new Date().toISOString();
      const info: IGenericFileInfo = { fileName: file.name, importedAt, count: records.length };
      // Lustro localStorage (offline/UI) — zapisem prawdy jest DB (powyżej).
      const entries = records.map((r) => ({ code: r.code, name: r.name, nip: r.nip }));
      try {
        resilientLocalStorage.setItem(CONTRACTORS_FILE_KEY, JSON.stringify(info));
        resilientLocalStorage.setItem(CONTRACTORS_ROWS_KEY, JSON.stringify({ entries }));
      } catch (err) {
        console.error("[Migracja] nie udało się zapisać lustra kontrahentów w localStorage:", err);
      }
      setContractorsInfo(info);
      window.dispatchEvent(new Event("contractors_updated"));
    } catch (err) {
      console.error("[Migracja] błąd importu kontrahentów:", err);
    } finally {
      setContractorsLoading(false);
    }
  };

  const handleJournalsChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    setJournalsLoading(true);
    try {
      const XLSX = await import("xlsx");
      const parser = getParser(selectedSystemId);
      const isCsv = /\.(csv|txt)$/i.test(file.name);
      const buffer = await file.arrayBuffer();
      let raw: CellValue[][];
      if (isCsv) {
        const bytes = new Uint8Array(buffer);
        const encoding = detectEncoding(bytes, parser);
        const text = new TextDecoder(encoding).decode(buffer);
        const firstLine = text.split(/\r?\n/)[0] ?? "";
        const sep = detectCsvSep(firstLine, parser);
        const wb = XLSX.read(text, { type: "string", FS: sep });
        raw = XLSX.utils.sheet_to_json<CellValue[]>(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: "" });
      } else {
        const wb = XLSX.read(buffer, { type: "array" });
        raw = XLSX.utils.sheet_to_json<CellValue[]>(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: "" });
      }
      // Spróbuj najpierw strukturalnego parsera Fakir (XLS z " - " w A1 i "Numer" w B1)
      const structured = parseFakirJournal(raw);
      let headers: string[];
      let rows: Record<string, unknown>[];
      if (structured) {
        // Format strukturalny — nagłówki już znormalizowane, gotowe do detectMapping
        headers = structured.headers;
        rows    = structured.rows.map((r, i) => ({ id: String(i), ...r }));
      } else {
        // Fallback: standardowy CSV z wierszem nagłówkowym (szukaj + scal dwupoziomowe)
        const parser2 = getParser(selectedSystemId);
        const journalHints = parser2.journalHints ?? GENERIC_JOURNAL_HINTS;
        const { headers: mergedHeaders, dataStartIdx } = readJournalHeaders(raw, journalHints);
        const headerMap: Array<{ name: string; colIdx: number }> = [];
        mergedHeaders.forEach((h, idx) => {
          const name = _cs(h);
          if (name) headerMap.push({ name, colIdx: idx });
        });
        headers = headerMap.map((m) => m.name);
        const dataRows = raw.slice(dataStartIdx).filter((r) => (r as CellValue[]).some((c) => c !== "" && c != null));
        rows = dataRows.map((r, i) => {
          const row: Record<string, unknown> = { id: String(i) };
          headerMap.forEach(({ name, colIdx }) => { row[name] = (r as CellValue[])[colIdx] ?? ""; });
          return row;
        });
      }
      const importedAt = new Date().toISOString();
      const info: IGenericFileInfo = { fileName: file.name, importedAt, count: rows.length };
      // v: 2 — format z właściwym separatorem i systemId; stare dane (v<2) są odrzucane
      const journalPayload = { v: 2, headers, rows, importedAt, fileName: file.name, systemId: selectedSystemId ?? undefined };

      // Zapis do DB (trwały dziennik) — nagłówki i wiersze jako JSON; zastępuje poprzedni import.
      try {
        await saveJournal({
          fileName: file.name,
          systemId: selectedSystemId ?? undefined,
          headersJson: JSON.stringify(headers),
          rowsJson: JSON.stringify(rows),
          rowCount: rows.length,
        });
        console.info(`[Dzienniki] zapisano ${rows.length} zapisów dziennika w DB.`);
      } catch (err) {
        console.error("[Dzienniki] nie udało się zapisać dziennika w DB:", err);
        alert("Nie udało się zapisać dziennika w bazie. Sprawdź połączenie i spróbuj ponownie.");
        return;
      }

      // Lustro localStorage (offline/UI) — zapisem prawdy jest DB (powyżej).
      try {
        localStorage.setItem(JOURNALS_FILE_KEY, JSON.stringify(info));
        localStorage.setItem(JOURNALS_ROWS_KEY, JSON.stringify(journalPayload));
      } catch { /* ignore — dane przekazywane przez CustomEvent i pamięć sesji */ }
      setJournalsInfo(info);
      window.dispatchEvent(new CustomEvent("journals_updated", { detail: journalPayload }));
    } catch (err) {
      console.error("[Dzienniki] błąd importu:", err);
    } finally {
      setJournalsLoading(false);
    }
  };

  // ── Meta linie dla kart ───────────────────────────────────────────────────

  const boMeta: string[] = [];
  if (boImportedAt) boMeta.push(`Wczytano: ${plDt(boImportedAt)}`);
  const boCount = boApiData?.entries?.length;
  if (boCount) boMeta.push(`${boCount.toLocaleString("pl-PL")} kont`);

  const contractorsMeta: string[] = [];
  if (contractorsInfo) {
    if (contractorsInfo.importedAt) contractorsMeta.push(`Wczytano: ${plDt(contractorsInfo.importedAt)}`);
    contractorsMeta.push(`${contractorsInfo.count.toLocaleString("pl-PL")} kontrahentów`);
  }

  const journalsMeta: string[] = [];
  if (journalsInfo) {
    if (journalsInfo.importedAt) journalsMeta.push(`Wczytano: ${plDt(journalsInfo.importedAt)}`);
    journalsMeta.push(`${journalsInfo.count.toLocaleString("pl-PL")} zapisów`);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Ukryte inputy plików */}
      <input ref={importBoRef}     type="file" accept=".xls,.xlsx,.ods,.csv,.txt" style={{ display: "none" }} onChange={handleBoFileChange} />
      <input ref={contractorsRef} type="file" accept=".xls,.xlsx,.ods,.csv,.txt" style={{ display: "none" }} onChange={handleContractorsChange} />
      <input ref={journalsRef}    type="file" accept=".xls,.xlsx,.ods,.csv,.txt" style={{ display: "none" }} onChange={handleJournalsChange} />

      <div style={{ padding: "24px 28px", display: "flex", flexDirection: "column", gap: 32, overflowY: "auto" }}>

        {/* ── Sekcja 1: System źródłowy ── */}
        <div>
          <SectionLabel step={1}>System źródłowy</SectionLabel>
          <FormControl size="small" sx={{ minWidth: 320 }}>
            <InputLabel>Wybierz system FK</InputLabel>
            <Select
              label="Wybierz system FK"
              value={selectedSystemId ?? ""}
              onChange={(e) => handleSystemSelect(e.target.value)}
            >
              {FIN_SYSTEMS.map((sys) => (
                <MenuItem key={sys.id} value={sys.id}>
                  {sys.vendor ? `${sys.vendor} – ${sys.name}` : sys.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* ── Sekcja 2: Pliki do migracji ── */}
        <div>
          <SectionLabel step={2}>Pliki do migracji</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

            <ImportRow
              icon={<AccountBalanceIcon sx={{ color: "#5d49c7", fontSize: 20 }} />}
              title="BO/ZOiS"
              hasData={hasBoData}
              metaLines={boMeta}
              loading={boImporting}
              onImport={() => importBoRef.current?.click()}
            />

            <ImportRow
              icon={<PeopleAltIcon sx={{ color: "#5d49c7", fontSize: 20 }} />}
              title="Kartoteka kontrahentów"
              hasData={!!contractorsInfo}
              metaLines={contractorsMeta}
              loading={contractorsLoading}
              onImport={() => contractorsRef.current?.click()}
            />

            <ImportRow
              icon={<ReceiptLongIcon sx={{ color: "#5d49c7", fontSize: 20 }} />}
              title="Dzienniki księgowań"
              hasData={!!journalsInfo}
              metaLines={journalsMeta}
              loading={journalsLoading}
              onImport={() => journalsRef.current?.click()}
            />

            {/* Wywodzenie schematów księgowania kontrahentów z dziennika. */}
            <div style={{ marginTop: 8, display: "flex", justifyContent: "flex-end" }}>
              <Button
                size="small"
                variant="outlined"
                disabled={backfillLoading}
                onClick={handleBackfillSchemes}
              >
                {backfillLoading ? "Wywodzę schematy…" : "Wywiedź schematy księgowania kontrahentów"}
              </Button>
            </div>

          </div>
        </div>
      </div>

      {/* ── Dialog potwierdzenia importu BO ── */}
      <Dialog open={!!boImport} onClose={() => setBoImport(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          Importuj bilans otwarcia
        </DialogTitle>
        {boImport && (
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
            <div style={{ fontSize: 13, color: "rgba(0,0,0,0.6)" }}>
              Plik: <strong>{boImport.fileName}</strong>
            </div>
            <div style={{ fontSize: 13 }}>
              Znaleziono <strong>{boImport.entries.length}</strong> kont.
            </div>
            <TextField
              label="Data bilansu otwarcia"
              type="date"
              size="small"
              fullWidth
              InputLabelProps={{ shrink: true }}
              value={boDate}
              onChange={(e) => setBoDate(e.target.value)}
              required
              error={!boDate}
              helperText={boDate
                ? "Data na jaką bilans otwarcia został sporządzony (rok wyznacza rok księgowy)"
                : "Wskaż datę bilansu otwarcia — bez niej nie można załadować."}
            />
            <div style={{ fontSize: 11, color: "rgba(0,0,0,0.45)" }}>
              Istniejące dane bilansu otwarcia zostaną zastąpione.
            </div>
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setBoImport(null)} color="inherit" size="small">Anuluj</Button>
          <Button onClick={handleBoConfirm} variant="contained" size="small" disabled={!boDate}>Załaduj</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MigrationTab;
